import { motion } from "framer-motion";
import { useEffect } from "react";
import heroVideo from "../assests/speech.mp4"; // 🎬 Import your background video

export default function TextSpeechQA() {
   
  return (
    <div className="bg-gray-50 text-gray-900 ">
      {/* Hero Section with Video Background */}
      <section className="relative w-full h-[70vh] text-white flex items-center justify-center text-center">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={heroVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70"></div>

        <div className="relative z-10 px-6 max-w-3xl">
          <motion.h1
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-bold mb-6 tracking-tight drop-shadow-lg font-orbitron"
          >
            Text & Speech QA Services
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed text-gray-200 font-raleway"
          >
            Ensure accuracy, consistency, and reliability in your NLP and speech
            AI models with expert QA services.
          </motion.p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-6 text-center max-w-4xl mx-auto">
        <h2 className="text-4xl font-semibold mb-6 text-gray-900 font-orbitron">
          Why Text & Speech QA Matters
        </h2>
        <p className="text-lg text-gray-700 leading-relaxed font-raleway">
          Quality Assurance (QA) in text and speech is critical for Natural
          Language Processing (NLP), speech recognition, and conversational AI
          systems. Poorly validated data can lead to biased results,
          misinterpretations, and system failures. Our QA process ensures that
          the datasets used to train your AI are clean, accurate, and reliable.
        </p>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-gray-100 via-white to-gray-100">
        <h2 className="text-4xl font-semibold text-center mb-12 text-gray-900 font-orbitron">
          Our QA Services
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-6xl mx-auto">
          {[
            {
              title: "📝 Text QA",
              desc: "Grammar, spelling, and semantic validation for clean and accurate text datasets.",
            },
            {
              title: "🎙 Speech QA",
              desc: "Accent, clarity, transcription, and pronunciation accuracy checks.",
            },
            {
              title: "🌍 Multilingual QA",
              desc: "Validation across multiple languages and dialects for global systems.",
            },
            {
              title: "🔍 Context & Consistency",
              desc: "Ensure text and speech align with meaning, tone, and domain.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              whileHover={{ scale: 1.05 }}
              className="bg-white p-6 rounded-2xl shadow-md hover:shadow-xl transition"
            >
              <h3 className="text-xl font-semibold mb-3 text-indigo-700">
                {item.title}
              </h3>
              <p className="text-gray-700 leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6">
        <h2 className="text-4xl font-semibold text-center mb-12 text-gray-900 font-orbitron">
          How We Work
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-6xl mx-auto text-center">
          {[
            "Receive & analyze raw text/speech data",
            "Apply linguistic, contextual & semantic validation",
            "Human + AI-assisted multi-layer QA",
            "Deliver high-quality validated datasets",
          ].map((step, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: idx * 0.2 }}
              viewport={{ once: true }}
              className="p-6 bg-white rounded-xl shadow-md hover:shadow-lg"
            >
              <div className="text-4xl mb-3">{`${idx + 1}️⃣`}</div>
              <p className="text-gray-700 leading-relaxed">{step}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Applications */}
      <section className="py-20 px-6 bg-gradient-to-r from-indigo-50 via-white to-purple-50">
        <h2 className="text-4xl font-semibold text-center mb-12 text-gray-900 font-orbitron">
          Applications
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {[
            {
              title: "🗣 Conversational AI",
              desc: "Accurate chatbot and virtual assistant responses.",
            },
            {
              title: "🎧 Speech Recognition",
              desc: "Improved transcription and voice assistant accuracy.",
            },
            {
              title: "📚 E-Learning",
              desc: "Clear and validated voiceovers and text content.",
            },
            {
              title: "🏥 Healthcare AI",
              desc: "Reliable medical transcriptions and reports.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              whileHover={{ scale: 1.05 }}
              className="bg-white p-6 rounded-2xl shadow hover:shadow-xl transition"
            >
              <h3 className="font-semibold mb-2 text-lg text-indigo-700">
                {item.title}
              </h3>
              <p className="text-gray-700 leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-6 text-center max-w-4xl mx-auto">
        <h2 className="text-4xl font-semibold mb-6 text-gray-900 font-orbitron">
          Why Choose Us
        </h2>
        <p className="text-lg text-gray-700 leading-relaxed font-raleawy">
          ✅ Human-in-the-loop validation for maximum accuracy <br />
          ✅ Expertise in 50+ languages & dialects <br />
          ✅ GDPR & data security compliant <br />
          ✅ Scalable QA for enterprise-level AI projects
        </p>
      </section>

      {/* Call to Action */}
      <section className="py-24 text-center bg-gradient-to-r from-indigo-600 to-purple-700 text-white">
        <h2 className="text-4xl md:text-5xl font-bold mb-6 font-orbitron">
          Build AI You Can Trust With Text & Speech QA
        </h2>
        <p className="text-lg max-w-xl mx-auto mb-6 font-raleway">
          Get in touch with us to ensure your datasets meet the highest quality
          standards.
        </p>
        <motion.button
          whileHover={{ scale: 1.1 }}
          className="bg-white text-indigo-700 font-semibold px-6 py-3 rounded-xl shadow-md hover:bg-gray-100 transition"
        >
          Contact Us
        </motion.button>
      </section>
    </div>
  );
}
